import React, { useEffect, useState } from 'react';
import { UtensilsCrossed, ShoppingBag, BedDouble, Landmark, Plane, Gem, Car, Building2, LayoutTemplate, FilePlus, Lock, Eye } from 'lucide-react';
import { toast } from 'sonner';
import { jsonApi } from '@/lib/jsonApi';
import { templateCounts } from '@/components/taxonomy/counts';
import TemplatePreview from '@/components/taxonomy/TemplatePreview';
import { Button } from '@/components/ui/button';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';

const ICONS = {
  restaurant: UtensilsCrossed, retail: ShoppingBag, hotel: BedDouble, banking: Landmark,
  travel: Plane, luxury_retail: Gem, automotive: Car, real_estate: Building2,
};

function Option({ icon: Icon, title, lines, chips, disabled, onPick, onPreview, testId, cta }) {
  return (
    <div className={`tx-reveal flex flex-col bg-white border rounded-2xl p-5 transition-[border-color,box-shadow] ${disabled ? 'border-[#E2E8F0] opacity-70' : 'border-[#D9E2F0] hover:border-[#1A3E6F] hover:shadow-[0_6px_24px_-12px_rgba(26,62,111,0.35)]'}`} data-testid={testId}>
      <div className="flex items-center gap-3">
        <span className="w-10 h-10 rounded-xl bg-[#F3F6FB] text-[#1A3E6F] flex items-center justify-center"><Icon className="w-5 h-5" /></span>
        <p className="font-bold text-[#0F172A] font-['Montserrat'] leading-tight">{title}</p>
      </div>
      <p className="text-xs text-[#64748B] mt-3 min-h-[32px]">{lines}</p>
      {chips?.length > 0 && <div className="flex flex-wrap gap-1 mt-3">{chips.map((c) => <span key={c} className="text-[10px] text-[#475569] bg-[#F1F5F9] rounded-full px-2 py-0.5">{c}</span>)}</div>}
      <div className="mt-auto pt-4 flex gap-2">
        {onPreview && !disabled && (
          <Button variant="outline" onClick={onPreview} className="shrink-0" data-testid={`${testId}-preview`}><Eye className="w-3.5 h-3.5 mr-1.5" /> Preview</Button>
        )}
        <Button onClick={onPick} disabled={disabled} variant={disabled ? 'outline' : 'default'} className={`flex-1 min-w-0 ${disabled ? '' : 'bg-[#1A3E6F] hover:bg-[#15325A]'}`} data-testid={`${testId}-btn`}>
          {disabled ? <><Lock className="w-3.5 h-3.5 mr-1.5" /> Not available yet</> : cta}
        </Button>
      </div>
    </div>
  );
}

const describe = (t) => (t.available
  ? templateCounts(t)
  : 'This template is still being prepared by satisFIND.');

export default function TemplatePicker({ canEdit, onDone }) {
  const [templates, setTemplates] = useState(null);
  const [choice, setChoice] = useState(null);
  const [preview, setPreview] = useState(null);
  useEffect(() => { jsonApi('/taxonomy/templates').then((d) => setTemplates(d.templates)).catch((e) => { toast.error(e.message); setTemplates([]); }); }, []);
  const apply = async () => {
    try {
      await jsonApi('/taxonomy/setup', 'POST', choice === 'empty' ? { start_empty: true } : { template_id: choice.template_id });
      toast.success(choice === 'empty' ? 'Started with an empty list' : `${choice.name} template copied to this client`);
      onDone();
    } catch (e) { toast.error(e.message); onDone(); }
  };
  if (!canEdit) {
    return <div className="bg-white border border-dashed border-[#CBD5E1] rounded-2xl p-10 text-center text-sm text-[#64748B]" data-testid="taxonomy-setup-readonly">Your admin hasn’t set up Attributes &amp; Factors for this account yet.</div>;
  }
  return (
    <section className="space-y-5" data-testid="taxonomy-setup">
      <div>
        <h2 className="text-base md:text-lg font-bold text-[#0F172A] font-['Montserrat']">Choose a starting point</h2>
        <p className="text-sm text-[#475569] mt-1 max-w-3xl">Pick the template closest to this client’s industry. The client gets its own editable copy — the master template never changes and no other client is affected. This choice is made once; afterwards you add, rename or switch off items directly.</p>
      </div>
      {!templates && <div className="py-10 text-center"><div className="loading-spinner mx-auto" /></div>}
      {templates && (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {templates.map((t) => (
            <Option key={t.template_id} icon={ICONS[t.key] || LayoutTemplate} title={t.name} lines={describe(t)} chips={t.available ? t.preview : []}
              disabled={!t.available} onPick={() => setChoice(t)} onPreview={() => setPreview({ t, open: true })} cta="Use template" testId={`template-option-${t.key || t.template_id}`} />
          ))}
          <Option icon={FilePlus} title="Start Empty" lines="Begin with a blank list and add your own Attributes and Factors." onPick={() => setChoice('empty')} cta="Start empty" testId="template-option-empty" />
        </div>
      )}
      <TemplatePreview template={preview?.t} open={!!preview?.open} onOpenChange={(v) => { if (!v) setPreview((p) => p && { ...p, open: false }); }} canUse
        onUse={(t) => { setPreview((p) => p && { ...p, open: false }); setChoice(t); }} />
      <AlertDialog open={!!choice} onOpenChange={(v) => { if (!v) setChoice(null); }}>
        <AlertDialogContent data-testid="taxonomy-setup-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>{choice === 'empty' ? 'Start with an empty list?' : `Use the ${choice?.name} template?`}</AlertDialogTitle>
            <AlertDialogDescription>
              {choice === 'empty'
                ? 'You will add every Attribute and Factor yourself.'
                : `${choice ? describe(choice) : ''} will be copied into this client’s own list. Later changes to the master template won’t change this copy.`}
              {' '}A starting template can only be chosen once.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="taxonomy-setup-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={apply} className="bg-[#1A3E6F] hover:bg-[#15325A]" data-testid="taxonomy-setup-confirm-btn">Confirm</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  );
}
